import React, { useState, useEffect } from "react";
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import { motion } from 'framer-motion';
import Theme from './Theme'; 
import PropTypes from "prop-types"
import IconButton from '@mui/material/IconButton'; 
import ArrowBackIcon from '@mui/icons-material/ArrowBack'; 
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import * as Turbo from "@hotwired/turbo";
import OnboardingStepper from './OnboardingStepper';
import SourcesManagement from './SourcesManagement';

// --- Main Wallets Page Component ---
const OnboardingWalletsPage = ({ currentCompany }) => {
  const [sourcesCount, setSourcesCount] = useState(0);
  const [isCompletingOnboarding, setIsCompletingOnboarding] = useState(false);

  const handleWalletConnect = (walletId) => {
    console.log(`Onboarding: Connecting to wallet ${walletId}...`);
    // TODO: Implement actual wallet connection logic for other wallets
    // For now, assume clicking a valid wallet goes to the next step
    handleContinue(); 
  };

  const handleContinue = () => {
    // Placeholder: Navigate to the next step (company setup)
    console.log("Navigating to company setup...");
    Turbo.visit(`/onboarding?step=company`); // Hardcoded for now
  };

  const handleBack = () => {
    // Navigate back to the settings step using Turbo
    console.log("Navigating back to settings...");
    const currentSearchParams = new URLSearchParams(window.location.search);
    currentSearchParams.set('step', 'settings');
    const backUrl = `/onboarding?${currentSearchParams.toString()}`;
    Turbo.visit(backUrl);
  };

  const handleFinishOnboarding = async () => {
    if (!currentCompany?.id || isCompletingOnboarding) return;

    setIsCompletingOnboarding(true);

    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;
      
      if (!csrfToken) {
        console.error("CSRF token not found!");
        alert("Session expired. Please refresh the page.");
        return;
      }

      const response = await fetch('/onboarding/complete', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': csrfToken
        },
        body: JSON.stringify({
          company_id: currentCompany.id
        })
      });

      if (response.ok) {
        // Always redirect to home with current company_id using Turbo
        const redirectUrl = `/?company_id=${currentCompany.id}`;
        Turbo.visit(redirectUrl);
      } else {
        const errorData = await response.json();
        console.error('Failed to complete onboarding:', errorData);
        alert(errorData.error || 'Failed to complete onboarding. Please try again.');
      }
    } catch (error) {
      console.error('Error completing onboarding:', error);
      alert('An error occurred. Please try again.');
    } finally {
      setIsCompletingOnboarding(false);
    }
  };

  const handleSourcesChange = (newSourcesCount) => {
    setSourcesCount(newSourcesCount);
  };

  // --- Render Logic ---
  return (
    <Theme>
      <React.Fragment>
        <Grid container component="main" sx={{ height: '100vh' }}>
          {/* Left Panel - Same structure as OnboardingSettingsPage */}
          <Grid
            item
            size={5} // Keeping the size prop as seen in the original Settings page structure
            sx={{
              background:
                'linear-gradient(211.21deg, #3376C2 29.32%, #2B62A1 95.41%)',
              position: 'relative',
              overflow: 'hidden',
              display: { xs: 'none', md: 'block' } // As per original Settings page
            }}
          >
            <Box
              position="absolute"
              top={108}
              left={59}
              component={motion.div}
              initial={{ opacity: 0, y: 100 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, type: 'spring', bounce: 0.25 }}
              zIndex={1}
            >
              <img src="/logo-large.png" alt="Contacryto" width={258} height={66} />
              <Typography
                variant="h4"
                mt={4}
                sx={{ color: 'common.white', maxWidth: '30rem' }}
              >
                Connect a Crypto Account { /* Updated Title */}
              </Typography>
              <Typography
                variant="body1" // Adjusted variant for subtitle consistency
                mt={2}
                sx={{ color: 'common.white', maxWidth: '30rem' }}
              >
                Select your crypto wallets { /* Updated Subtitle */}
              </Typography>
            </Box>
            {/* Optional: Add bottom graphic if available */}
            <Box
              position="absolute"
              right={0}
              bottom={-4} 
              component={motion.div}
              initial={{ opacity: 0, scale: 0.9, x: -600, y: 300 }}
              animate={{ opacity: 1, scale: 1, x: 0, y: 0 }}
              transition={{ duration: 1, type: 'spring', bounce: 0.25 }}
            >
              {/* Placeholder - Add image here if needed */}
              {/* e.g., <img src="/path/to/wallet-graphics.png" /> */}
            </Box> 
          </Grid>

          {/* Right Panel - Same structure as OnboardingSettingsPage */} 
          <Grid
            item
            sx={{
              height: '100vh',
              overflowY: 'auto',
              position: 'relative',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              bgcolor: '#F9FAFC', // Light background for right panel from image
              padding: { xs: 2, md: 4 }, // Adjusted padding
              flexGrow: 1
            }}
          >
            <OnboardingStepper activeStep={3} />
            {/* Inner container */} 
            <Box 
              sx={{ 
                width: '100%', 
                maxWidth: '700px', // Adjusted width for wallet grid
                mx: 'auto', 
                display: 'flex',
                flexDirection: 'column',
                flexGrow: 1 
              }}
            >
              {/* Top Controls: Back and Step Indicator */} 
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, width: '100%' }}>
                <Button 
                  startIcon={<ArrowBackIcon />} 
                  onClick={handleBack} 
                  size="small"
                >
                  Back
                </Button>
                
                {/* Show Finish button when there's at least 1 source */}
                {sourcesCount >= 1 && (
                  <Button
                    variant="contained"
                    color="primary"
                    size="large"
                    startIcon={<CheckCircleIcon />}
                    onClick={handleFinishOnboarding}
                    disabled={isCompletingOnboarding}
                    sx={{
                      px: 4,
                      py: 1,
                      fontWeight: 'bold',
                      borderRadius: 2,
                      textTransform: 'none'
                    }}
                  >
                    {isCompletingOnboarding ? 'Completing...' : 'Finish Setup'}
                  </Button>
                )}
              </Box>

              {/* Titles and Instructions */} 
              <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 'bold' }}>
                 Select Crypto Accounts { /* Title from image */}
              </Typography>
              <Typography variant="body1" gutterBottom sx={{ mb: 3, color: 'text.secondary' }}>
                 Instructions: Select the crypto wallets or exchanges you use. Add at least one to continue.
              </Typography>

              {/* Sources Management Component */}
              <SourcesManagement 
                currentCompany={currentCompany}
                onWalletConnect={handleWalletConnect}
                onSourcesChange={handleSourcesChange}
              />
              
              {/* Spacer added to push footer down if content is short */}
              <Box sx={{ flexGrow: 1 }} /> 

              {/* Footer (Consistent with other steps) */} 
              <Typography variant="caption" display="block" sx={{ mt: 'auto', pt: 2, textAlign: 'center', color: 'text.secondary' }}>
                 Copyright © Contacrypto 2025
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </React.Fragment>
    </Theme>
  );
}

OnboardingWalletsPage.propTypes = {
  currentCompany: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  })
};

OnboardingWalletsPage.defaultProps = {
  currentCompany: null
};

export default OnboardingWalletsPage; 
